"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAuth = void 0;
const react_1 = require("react");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
const useAuth = (options) => {
    const [isLoading, setIsLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const [nextStep, setNextStep] = (0, react_1.useState)(null);
    const handleSuccess = (0, react_1.useCallback)(() => {
        setError(null);
        if (options?.onSuccess) {
            options.onSuccess();
        }
    }, [options]);
    const handleError = (0, react_1.useCallback)((err) => {
        setError(err);
        if (options?.onError) {
            options.onError(err);
        }
    }, [options]);
    const signIn = (0, react_1.useCallback)(async (username, password) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await amplifyBootstrap_1.Auth.signIn(username, password);
            setNextStep(result.nextStep?.signInStep || null);
            if (result.isSignedIn) {
                handleSuccess();
            }
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            handleError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [handleSuccess, handleError]);
    const signInWithRedirect = (0, react_1.useCallback)(async (provider) => {
        setIsLoading(true);
        setError(null);
        try {
            await amplifyBootstrap_1.Auth.signInWithRedirect({
                provider,
            });
            handleSuccess();
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            handleError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [handleSuccess, handleError]);
    const confirmSignIn = (0, react_1.useCallback)(async (challengeResponse) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await amplifyBootstrap_1.Auth.confirmSignIn({
                challengeResponse,
            });
            if (result.isSignedIn) {
                handleSuccess();
            }
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            handleError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);
    const signUp = (0, react_1.useCallback)(async (username, password, attributes) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await amplifyBootstrap_1.Auth.signUp(username, password, attributes);
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            handleError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [handleError]);
    const confirmSignUp = (0, react_1.useCallback)(async (username, code) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await amplifyBootstrap_1.Auth.confirmSignUp(username, code);
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            handleError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [handleError]);
    const forgotPassword = (0, react_1.useCallback)(async (username) => {
        setIsLoading(true);
        setError(null);
        try {
            await amplifyBootstrap_1.Auth.forgotPassword(username);
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            handleError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [handleError]);
    const forgotPasswordSubmit = (0, react_1.useCallback)(async (username, code, newPassword) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await amplifyBootstrap_1.Auth.forgotPasswordSubmit(username, code, newPassword);
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            handleError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [handleError]);
    const signOut = (0, react_1.useCallback)(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await amplifyBootstrap_1.Auth.signOut();
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            handleError(error);
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, [handleError]);
    const currentSession = amplifyBootstrap_1.Auth.currentSession;
    const currentAuthenticatedUser = amplifyBootstrap_1.Auth.currentAuthenticatedUser;
    const fetchUserAttributes = amplifyBootstrap_1.Auth.fetchUserAttributes;
    const updateUserAttribute = amplifyBootstrap_1.Auth.updateUserAttribute;
    const disableTOTP = amplifyBootstrap_1.Auth.disableTOTP;
    const fetchMFAStatus = amplifyBootstrap_1.Auth.fetchMFAStatus;
    return {
        signIn,
        signInWithRedirect,
        confirmSignIn,
        signUp,
        confirmSignUp,
        forgotPassword,
        forgotPasswordSubmit,
        signOut,
        isLoading,
        error,
        nextStep,
        currentSession,
        currentAuthenticatedUser,
        fetchUserAttributes,
        updateUserAttribute,
        disableTOTP,
        fetchMFAStatus,
    };
};
exports.useAuth = useAuth;
